// Form Fill otomatik kayıt döngüsü — form-fill-store'dan ayrıldı (600 satır kuralı).
// Debounce flush (800ms) + gerçek ağ/5xx hatasında exp-backoff + localStorage kuyruğu.
//
// UX P1 K4 (2026-10-27) — öğe bazlı kayıt izolasyonu:
//   • Sunucu geçersiz öğeyi ayıklar, geçerlileri yazar (422 VALIDATION_FAILED + `savedCodes`,
//     bkz. backend services/responses.ts). Burada `savedCodes` başarı yoluyla aynı şekilde
//     kuyruktan düşer; reddedilen öğe kuyrukta KALIR ve `errorsByCode`'a yazılır.
//   • Reddedilmiş ve o andan beri DEĞİŞMEMİŞ öğe ("tutulan" öğe) sonraki flush'lara
//     gönderilmez — kullanıcı alanı düzeltince setValue hatayı temizler, öğe yeniden gider.
//   • Yalnız tutulan öğeler kaldıysa durum "invalid" ("N alan kaydedilemedi — düzeltin").
//     "retry" YALNIZ gerçekten bir yeniden deneme zamanlandığında yazılır.
//   • Kuyruk localStorage'dan geri yüklenince hata işaretleri yoktur → ilk flush hepsini
//     gönderir, sunucu geçerlileri yazar, geçersizi yeniden işaretler (sayfa yenileme kilitlemez).

import { apiRaw } from "../api/client";
import { FORM_RESPONSE_BATCH_MAX } from "@mahir/shared";
import type { FormResponseBatchUpsertResult, FormResponseUpsertItem } from "@mahir/shared";
import type { FormFillState, PendingChange } from "./form-fill-store";

type Get = () => FormFillState;
type SetState = (patch: Partial<FormFillState>) => void;
interface ErrorEntry {
  partKey?: string;
  message: string;
}

export const DEBOUNCE_MS = 800;
export const RETRY_DELAYS_MS = [1_000, 3_000, 8_000, 20_000];
export const SAVE_TIMEOUT_MS = 30_000;
const QUEUE_STORAGE_KEY = (projectId: string): string => `mahir.form-fill.queue.${projectId}`;

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
// UX P1 I2 — tek uçuş: aynı anda en fazla bir PUT. Uçuştayken gelen flush istekleri
// `rerunRequested` bayrağını kaldırır ve AYNI söze (promise) bağlanır; yanıt uygulanınca
// döngü bir tur daha koşar. Böylece yavaş eski istek yeni değeri ezemez ve `flushNow`
// bekleyen her şey gönderilene kadar bekler.
let inFlight: Promise<void> | null = null;
let rerunRequested = false;
// Gerçek yeniden deneme: ağ kopukluğu (0), zaman aşımı (408), hız sınırı (429), 5xx.
// Diğer 4xx kalıcıdır — dört deneme yakıp "Çevrimdışı" demek yanlış teşhis olur (I1).
const isRetryableStatus = (status: number): boolean =>
  status === 0 || status === 408 || status === 429 || status >= 500;

export function clearFlushTimers(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

export function scheduleFlush(get: Get, setState: SetState, delayMs = DEBOUNCE_MS): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void flushPending(get, setState);
  }, delayMs);
}

/** Sunucunun reddettiği ve o andan beri değişmemiş (gönderilmeyecek) bekleyen soru kodları. */
export function selectHeldInvalidCodes(
  state: Pick<FormFillState, "pending" | "errorsByCode">,
): string[] {
  return Object.keys(state.pending).filter((code) => state.errorsByCode[code] !== undefined);
}

/**
 * Ö7 — kaydedilmemiş ve kendiliğinden kaydedilmeyecek değişiklik sayısı: tutulan geçersiz
 * öğeler + çevrimdışı/yeniden deneme/çakışma durumunda bekleyen her şey. Olağan debounce
 * (typing/saving) sayılmaz — rozet her tuşta yanıp sönmesin.
 */
export function selectStuckUnsavedCount(
  state: Pick<FormFillState, "pending" | "errorsByCode" | "saveState">,
): number {
  if (
    state.saveState === "offline" ||
    state.saveState === "retry" ||
    state.saveState === "conflict" ||
    state.saveState === "failed"
  ) {
    return Object.keys(state.pending).length;
  }
  return selectHeldInvalidCodes(state).length;
}

export function flushPending(get: Get, setState: SetState): Promise<void> {
  if (inFlight) {
    rerunRequested = true;
    return inFlight;
  }
  inFlight = (async () => {
    try {
      do {
        rerunRequested = false;
        await flushOnce(get, setState);
        // Yeniden deneme/çevrimdışı/çakışma/kalıcı hata sonrası anında tekrar YOK —
        // backoff zamanlayıcısı ya da kullanıcının sonraki düzenlemesi tetikler.
      } while (
        rerunRequested &&
        !["retry", "offline", "conflict", "failed"].includes(get().saveState)
      );
    } finally {
      inFlight = null;
      rerunRequested = false;
    }
  })();
  return inFlight;
}

async function flushOnce(get: Get, setState: SetState): Promise<void> {
  dropUnknownPending(get, setState);
  const state = get();
  if (state.saveState === "conflict") return;
  const allPending = Object.values(state.pending);
  if (allPending.length === 0 || !state.projectId) return;

  // Tutulan geçersiz öğeler gönderilmez — her flush'ta aynı 422'yi yeniden üretmesin.
  // Parti tavanı (500) aşılırsa kalan öğeler bir sonraki tura kalır ("typing" → debounce).
  const sendable = allPending
    .filter((p) => state.errorsByCode[p.questionCode] === undefined)
    .slice(0, FORM_RESPONSE_BATCH_MAX);
  if (sendable.length === 0) {
    setState({ saveState: "invalid" });
    return;
  }

  if (!state.isOnline) {
    setState({ saveState: "offline" });
    return;
  }

  const prevState = state.saveState;
  setState({ saveState: prevState === "syncing" ? "syncing" : "saving" });

  const payload: FormResponseUpsertItem[] = sendable.map((p) => ({
    questionCode: p.questionCode,
    value: p.value,
    consultantNote: p.consultantNote,
    clientUpdatedAt: p.clientUpdatedAt,
  }));

  // fetch ağ hatasında FIRLATIR (ör. "Failed to fetch") — eskiden yakalanmıyordu, durum
  // "saving"de takılı kalıyordu. Ağ hatası = status 0 → aşağıdaki gerçek yeniden deneme.
  // Re-review — tek uçuşta asılı kalan istek tüm kayıtları bekletmesin: 30 sn sonra iptal,
  // ağ hatası sayılır (status 0 → yeniden deneme).
  let res: { ok: boolean; status: number; body: FormResponseBatchUpsertResult };
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), SAVE_TIMEOUT_MS);
  try {
    res = await apiRaw<FormResponseBatchUpsertResult>(`/projects/${state.projectId}/responses`, {
      method: "PUT",
      body: { responses: payload },
      signal: controller.signal,
    });
  } catch {
    res = { ok: false, status: 0, body: { success: false } };
  } finally {
    clearTimeout(timeout);
  }

  const superseded = res.body?.superseded ?? [];
  if (res.ok && res.body?.success) {
    const skipped = new Set(superseded.map((x) => x.questionCode));
    applySaveResult(get, setState, sendable, {
      savedCodes: new Set(payload.map((p) => p.questionCode).filter((c) => !skipped.has(c))),
      savedAt: res.body.savedAt ?? null,
      rejected: [],
      superseded,
    });
    return;
  }

  if (res.status === 409 && res.body?.errorCode === "STALE_WRITE") {
    setState({
      saveState: "conflict",
      conflictDetails: (res.body.details ?? []).map((d) => ({
        questionCode: d.questionCode,
        message: d.message,
        ...(d.serverUpdatedAt ? { serverUpdatedAt: d.serverUpdatedAt } : {}),
      })),
    });
    return;
  }

  if (res.status === 422 && res.body?.errorCode === "VALIDATION_FAILED") {
    // Doğrulama hatası → yeniden deneme anlamsız (kullanıcı düzeltmeli). Geçerli öğeler
    // sunucuda yazıldı (`savedCodes`); reddedilenler kartında sunucu mesajıyla kalır.
    applySaveResult(get, setState, sendable, {
      savedCodes: new Set(res.body.savedCodes ?? []),
      savedAt: res.body.savedAt ?? null,
      rejected: res.body.details ?? [],
      superseded,
    });
    return;
  }

  // eng-review D7/D11 — participation sona erdiyse (submit/revoke) PUT 403
  // {error:"PARTICIPATION_ENDED"} döner (ResponsesServiceError yolu, "errorCode"
  // DEĞİL "error" alanı — services/responses.ts). api/client.ts merkezi kickout'u
  // zaten tetikledi (navigate); burada tek iş retry döngüsünü DURDURMAK.
  if (
    res.status === 403 &&
    (res.body as unknown as { error?: string } | null)?.error === "PARTICIPATION_ENDED"
  ) {
    setState({ saveState: "offline", errorMessage: "Form erişiminiz kapandı" });
    return;
  }

  // Kalıcı istemci hatası (400/401/403/404/413/422-parti…) → yeniden deneme YOK. Kuyruk
  // localStorage'da durur; kullanıcının sonraki düzenlemesi ya da sayfa yenileme yeniden dener.
  if (!isRetryableStatus(res.status)) {
    setState({ saveState: "failed", retryAttempt: 0, errorMessage: null });
    return;
  }

  // Ağ ya da sunucu hatası → gerçek yeniden deneme (exp-backoff)
  const attempt = get().retryAttempt;
  if (attempt >= RETRY_DELAYS_MS.length) {
    setState({ saveState: "offline", retryAttempt: 0 });
    return;
  }
  const delay = RETRY_DELAYS_MS[attempt] ?? 20_000;
  setState({
    saveState: "retry",
    retryAttempt: attempt + 1,
    errorMessage: `Yeniden deneniyor (${attempt + 1}/${RETRY_DELAYS_MS.length})…`,
  });
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void flushPending(get, setState);
  }, delay);
}

/**
 * Başarı ve kısmi kabulün ortak uzlaştırması (`sent` = gönderilen bekleyen nesnelerin
 * KENDİSİ). Bir bekleyen öğe yalnız sunucu onu YAZDIYSA
 * ve gönderildiğinden beri değişmediyse kuyruktan düşer — uçuş sırasında düzenlenen ya da
 * hiç gönderilmeyen (tutulan, yeni eklenen) öğeler kalır. (Eski başarı dalı gönderilmemiş
 * öğeleri de siliyordu.) Red işareti de yalnız değişmemiş öğeye yazılır: uçuş sırasında
 * düzeltilen alan bayat mesajla tutulmaz.
 */
function applySaveResult(
  get: Get,
  setState: SetState,
  sent: PendingChange[],
  result: {
    savedCodes: Set<string>;
    savedAt: string | null;
    rejected: NonNullable<FormResponseBatchUpsertResult["details"]>;
    superseded: NonNullable<FormResponseBatchUpsertResult["superseded"]>;
  },
): void {
  const cur = get();
  const sentByCode = new Map(sent.map((p) => [p.questionCode, p]));
  // Referans eşitliği: setValue/setNote/undo her yazımda YENİ nesne üretir. (Zaman damgası
  // karşılaştırması aynı milisaniyedeki iki yazımı ayırt edemezdi.)
  const unchangedSince = (code: string): boolean => {
    const s = sentByCode.get(code);
    return s !== undefined && cur.pending[code] === s;
  };

  // I2 — atlanan (aynı kullanıcının daha yeni yazımı indiği) öğe de sunucuda "tamam"dır.
  const supersededCodes = new Set(result.superseded.map((x) => x.questionCode));
  const remaining: Record<string, PendingChange> = {};
  for (const [code, p] of Object.entries(cur.pending)) {
    const acknowledged = result.savedCodes.has(code) || supersededCodes.has(code);
    if (!(acknowledged && unchangedSince(code))) remaining[code] = p;
  }

  const responses = { ...cur.responsesByCode };
  const errors: Record<string, ErrorEntry[]> = { ...cur.errorsByCode };
  for (const code of result.savedCodes) {
    const p = sentByCode.get(code);
    const q = cur.snapshot?.snapshot.questions.find((qq) => qq.code === code);
    if (p && q) {
      responses[code] = {
        questionCode: code,
        questionType: q.type,
        value: p.value,
        consultantNote: p.consultantNote ?? null,
        updatedAt: result.savedAt ?? new Date().toISOString(),
      };
    }
    // B4 — sunucu kabul etti: bayat kart-içi hata satırı kalmasın
    delete errors[code];
  }
  // R1 — atlanan yazım: sunucudaki değer gönderdiğimizden FARKLIYSA sessizce "Kaydedildi"
  // denmez; kartta bildirim + kullanıcının değeri "Benimkini uygula" için saklanır. Uçuşta
  // yeniden düzenlenen alan için bildirim yok (yeni değer zaten bir sonraki turda gider).
  const notices = { ...cur.supersededByCode };
  for (const x of result.superseded) {
    const sentItem = sentByCode.get(x.questionCode);
    if (
      sentItem &&
      unchangedSince(x.questionCode) &&
      !(
        sameJson(sentItem.value ?? null, x.value) &&
        (sentItem.consultantNote ?? null) === (x.consultantNote ?? null)
      )
    ) {
      notices[x.questionCode] = {
        mine: sentItem.value,
        mineNote: sentItem.consultantNote,
        storedClientUpdatedAt: x.clientUpdatedAt,
      };
    }
    const q = cur.snapshot?.snapshot.questions.find((qq) => qq.code === x.questionCode);
    if (q) {
      responses[x.questionCode] = {
        questionCode: x.questionCode,
        questionType: q.type,
        value: x.value,
        consultantNote: x.consultantNote,
        updatedAt: x.updatedAt,
      };
    }
    delete errors[x.questionCode];
  }
  const rejectedNow: Record<string, ErrorEntry[]> = {};
  for (const d of result.rejected) {
    if (!unchangedSince(d.questionCode)) continue;
    const entry: ErrorEntry = { message: d.message };
    if (d.partKey !== undefined) entry.partKey = d.partKey;
    (rejectedNow[d.questionCode] ??= []).push(entry);
  }
  Object.assign(errors, rejectedNow);

  const sendableLeft = Object.keys(remaining).filter((c) => errors[c] === undefined).length;
  const heldLeft = Object.keys(remaining).length - sendableLeft;
  const hasNotice = Object.keys(notices).length > 0;
  const nextState: FormFillState["saveState"] =
    sendableLeft > 0 ? "typing" : heldLeft > 0 ? "invalid" : hasNotice ? "idle" : "saved";

  setState({
    pending: remaining,
    responsesByCode: responses,
    saveState: nextState,
    lastSavedAt: result.savedCodes.size > 0 ? (result.savedAt ?? cur.lastSavedAt) : cur.lastSavedAt,
    retryAttempt: 0,
    errorMessage: null,
    errorsByCode: errors,
    supersededByCode: notices,
  });
  persistQueue(get);

  // Ö7 — kayıt (kısmi de olsa) eksik listesini değiştirmiş olabilir → sunucudan yenile
  if (result.savedCodes.size > 0) void get().refreshMissing();

  if (nextState === "saved") {
    setTimeout(() => {
      if (get().saveState === "saved") setState({ saveState: "idle" });
    }, 2000);
  } else if (nextState === "typing") {
    scheduleFlush(get, setState);
  }
}

/** Derin eşitlik — jsonb nesne anahtarlarını yeniden sıralar, JSON.stringify karşılaştırması yetmez. */
export function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bb = b as unknown[];
    return a.length === bb.length && a.every((v, i) => sameJson(v, bb[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const ak = Object.keys(ao);
  if (ak.length !== Object.keys(bo).length) return false;
  return ak.every((k) => k in bo && sameJson(ao[k], bo[k]));
}

/**
 * İnceleme M1 — snapshot'ta olmayan kodla bekleyen öğe (eski kuyruk, değişmiş proje) sunucuda
 * hep reddedilir ve hiçbir kartta görünmeden kaydı/gönderimi kilitlerdi. Snapshot yüklüyse
 * böyle öğeler kuyruktan atılır (konsola uyarı).
 */
function dropUnknownPending(get: Get, setState: SetState): void {
  const st = get();
  const questions = st.snapshot?.snapshot.questions;
  if (!questions) return;
  const known = new Set(questions.map((q) => q.code));
  const unknown = Object.keys(st.pending).filter((c) => !known.has(c));
  if (unknown.length === 0) return;
  console.warn("[form-fill] snapshot'ta olmayan bekleyen yanıtlar atıldı:", unknown);
  const pending = { ...st.pending };
  const errorsByCode = { ...st.errorsByCode };
  for (const c of unknown) {
    delete pending[c];
    delete errorsByCode[c];
  }
  setState({ pending, errorsByCode });
  persistQueue(get);
}

export function persistQueue(get: Get): void {
  if (typeof localStorage === "undefined") return;
  const state = get();
  if (!state.projectId) return;
  const key = QUEUE_STORAGE_KEY(state.projectId);
  if (Object.keys(state.pending).length === 0) {
    localStorage.removeItem(key);
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(state.pending));
  } catch {
    // quota exceeded vb. sessiz geç
  }
}

export function restoreQueue(projectId: string): Record<string, PendingChange> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY(projectId));
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, PendingChange>;
  } catch {
    return {};
  }
}

export function clearQueue(projectId: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(QUEUE_STORAGE_KEY(projectId));
}
